// PremiumDueReport.jsx
import React, { useState, useRef, useEffect } from "react";
import { FiChevronDown } from "react-icons/fi";
import { Mail } from "lucide-react";
import { FaFilePdf, FaWhatsapp, FaWhatsappSquare, FaRegFilePdf } from "react-icons/fa";
import { RiFileExcel2Line, RiWhatsappLine } from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import { useGetGroupsQuery, useLazyPreviewPremiumDueReportQuery, useExportPremiumDueReportMutation, useGetExportStatusQuery, useDownloadExportQuery } from "../../../../../services/redux/api";
import { API_BASE_URL } from '../../../../../services/config';
import ReportLoader from '../LifePolicyRegister/ReportLoader';

/* Tailwind tokens */
const fieldBase =
  "rounded-md border border-gray-200 px-3 py-2 shadow-sm focus:outline-none focus:ring-1 focus:ring-sky-200";
const labelBase = "text-gray-700";
const btnPrimary =
  "bg-brandRed hover:bg-red-600 text-white font-medium px-6 py-2 rounded-md shadow-md transition";

/* outside click hook for dropdown */
function useOutsideClick(ref, handler) {
  useEffect(() => {
    const onClick = (e) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target)) handler();
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [ref, handler]);
}

/**
 * Normalize mutation/query hook shapes.
 */
function normalizeHook(hook) {
  let trigger;
  let result = {};

  if (Array.isArray(hook)) {
    trigger = hook[0];
    result = hook[1] ?? {};
  } else if (hook && typeof hook === 'object') {
    trigger = hook.trigger ?? hook.mutate ?? hook;
    result = {
      data: hook.data,
      isLoading: hook.isLoading ?? hook.isFetching ?? hook.status === 'pending',
      error: hook.error,
      status: hook.status,
    };
  } else {
    trigger = undefined;
    result = {};
  }

  return { trigger, result };
}

/* Compact multi-select */
function MultiSelectInline({ options = [], value = [], onChange, placeholder = "Select groups..." }) {
    const [open, setOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const rootRef = useRef(null);
    useOutsideClick(rootRef, () => setOpen(false));

    const toggle = (id) => {
        if (value.includes(id)) onChange(value.filter((v) => v !== id));
        else onChange([...value, id]);
    };

    const filteredOptions = options.filter((o) =>
        o.label.toLowerCase().includes(searchTerm.toLowerCase())
    );  return (
    <div ref={rootRef} className="relative w-full">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className={`${fieldBase} min-h-[40px] py-2 w-full flex items-center gap-2 justify-between bg-white`}
      >
        <div className="flex items-center gap-2 flex-1 flex-wrap max-h-24 overflow-y-auto">
          {value.length === 0 ? (
            <span className="text-gray-400  truncate">{placeholder}</span>
          ) : (
            <div className="flex items-center gap-1 flex-wrap">
              {value.map((id) => {
                const o = options.find((x) => x.id === id);
                return (
                  <span
                    key={id}
                    className="text-xs bg-gray-100 px-2 py-0.5 rounded-md text-gray-700 whitespace-nowrap"
                  >
                    {o?.label ?? id}
                  </span>
                );
              })}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-xs text-gray-500">{value.length > 0 ? `${value.length} selected` : ""}</span>
          <FiChevronDown className="text-gray-500" />
        </div>
      </button>

      {open && (
        <div className="absolute z-30 mt-2 w-full bg-white border border-gray-200 shadow-lg rounded-md overflow-hidden">
          <div className="p-2 border-b border-gray-200">
            <input
              type="text"
              placeholder="Search groups..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div className="max-h-52 overflow-auto p-2">
            {filteredOptions.length === 0 ? (
              <div className="text-gray-400 text-center py-2">No groups found</div>
            ) : (
              filteredOptions.map((o) => (
                <label key={o.id} className="flex items-center gap-3 px-2 py-2 hover:bg-gray-50 rounded-md cursor-pointer">
                  <input type="checkbox" checked={value.includes(o.id)} onChange={() => toggle(o.id)} className="w-4 h-4" />
                  <span className=" text-gray-700">{o.label}</span>
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function PremiumDueReport() {
  const [fromDueDate, setFromDueDate] = useState("");
  const [toDueDate, setToDueDate] = useState("");
  const [paymentMode, setPaymentMode] = useState("");
  const [policyStatus, setPolicyStatus] = useState("");
  const [includeLapsed, setIncludeLapsed] = useState(false);
  const [groupsSelected, setGroupsSelected] = useState([]);
  const [sortBy, setSortBy] = useState("group");
  const [reportFields, setReportFields] = useState({ address: false, mobile: false, email: false });
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfObjectUrl, setPdfObjectUrl] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [jobStatusText, setJobStatusText] = useState('');
  const [jobCreating, setJobCreating] = useState(false);
  const navigate = useNavigate();

  // RTK Query hooks
  const [triggerPreview, previewResult] = useLazyPreviewPremiumDueReportQuery();
  const { data: groupsResult, isFetching: groupsLoading } = useGetGroupsQuery({ pageNumber: 1, pageSize: 1000 });

  // Export job hooks
  const [createExportJob, createJobResult] = useExportPremiumDueReportMutation();
  
  const { data: jobStatusData } = useGetExportStatusQuery(jobId, { pollingInterval: 2000, skip: !jobId });
  
  const { refetch: triggerDownload } = useDownloadExportQuery(jobId, { skip: true });

  // const groupsOptions = React.useMemo(() => {
  //   const rows = groupsResult?.data;
  //   console.log("groupsResult", groupsResult);
  //   if (!rows || !Array.isArray(rows)) return [];
  //   return rows.map((g) => ({
  //     id: g.GroupID ?? g.id ?? String(g.GroupCode ?? g.GroupName ?? ''),
  //     label: g.GroupName ?? g.GroupCode ?? String(g.GroupID ?? g.id ?? ''),
  //   }));
  // }, [groupsResult]);


  const groupsOptions = React.useMemo(() => {
    const rows = groupsResult?.data;
    if (!rows || !Array.isArray(rows)) return [];

    return rows.map((g) => ({
      id: String(g.GroupId ?? g.id ?? ''), // ✅ Always use GroupID for API value
      label: g.GroupName ?? g.GroupCode ?? String(g.GroupID ?? g.id ?? ''), // just for UI
    }));
  }, [groupsResult]);



  const toggleReportField = (k) => setReportFields((s) => ({ ...s, [k]: !s[k] }));

  const mapSortForBackend = (localSort) => {
    switch (localSort) {
      case "group": return "groupwise";
      case "member": return "memberwise";
      case "area": return "areawise";
      case "due": return "duedate";
      default: return "groupwise";
    }
  };

  const buildPayload = () => {
    return {
      fromDate: fromDueDate || null,
      toDate: toDueDate || null,
      includeLapsed: includeLapsed ? '1' : '0',
      groupIds: groupsSelected && groupsSelected.length ? groupsSelected.join(",") : null,
      sortBy: mapSortForBackend(sortBy),
      includeAddress: reportFields.address ? '1' : '0',
      includeMobile: reportFields.mobile ? '1' : '0',
      includeEmail: reportFields.email ? '1' : '0',
      filterByDate: "fup",
      reportTitle: 'Premium Due Report',
      sortingOrder: mapSortForBackend(sortBy)
    };
  };

  // Cleanup effect
  React.useEffect(() => () => { if (pdfObjectUrl) URL.revokeObjectURL(pdfObjectUrl); }, [pdfObjectUrl]);

  // Monitor job status
  React.useEffect(() => {
    if (jobStatusData?.job) {
      const j = jobStatusData.job;
      setJobStatusText(j.status + (j.error ? ` — ${j.error}` : ''));
      if (j.status === 'done' && j.filePath) {
        downloadGeneratedFile(jobId).catch((e) => console.error('download error', e));
      } else if (j.status === 'failed') {
        setJobCreating(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobStatusData]);

  // Download generated file
  const downloadGeneratedFile = async (id) => {
    try {
      if (triggerDownload) {
        const dlRes = await triggerDownload();
        const blob = dlRes?.data;
        if (blob instanceof Blob) {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `premium_due_${id}.pdf`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          URL.revokeObjectURL(url);
        } else if (dlRes?.url) {
          window.location.href = dlRes.url;
        } else {
          window.location.href = `${API_BASE_URL}/policy-management/export/download/${id}`;
        }
      } else {
        window.location.href = `${API_BASE_URL}/policy-management/export/download/${id}`;
      }
    } catch (err) {
      console.error('Error downloading file', err);
      window.location.href = `${API_BASE_URL}/policy-management/export/download/${id}`;
    }
  };

  // Create export job and download
  const handleCreateExportJobAndDownload = async () => {
    setJobCreating(true);
    setJobStatusText('Creating export job...');
    try {
      if (pdfObjectUrl) {
        const filename = `premium_due_preview_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.pdf`;
        const a = document.createElement('a');
        a.href = pdfObjectUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setJobCreating(false);
        setJobStatusText('Downloaded preview PDF');
        return;
      }
      if (typeof createExportJob !== 'function') throw new Error('Create job hook not available');
      const exportParams = buildPayload();
      exportParams.reportTitle = 'Premium Due Report';
      exportParams.reportType = 'premium-due';
      exportParams.format = 'pdf';
      console.log('[PremiumDueReport] Calling createExportJob with params:', exportParams);
      const res = await createExportJob(exportParams).unwrap();
      const payload = res?.data ?? res;
      const newJobId = payload?.jobId ?? payload?.job?.id ?? payload?.id;
      if (!newJobId) throw new Error('No jobId returned from create export job');
      setJobId(newJobId);
      setJobStatusText(`Job created: ${newJobId}. Waiting for file...`);
    } catch (err) {
      console.error('Export job error', err);
      alert('Export job failed: ' + (err?.message ?? err));
      setJobCreating(false);
    }
  };

  const handlePdfPreview = async () => {
    try {
      setIsGenerating(true);
      const payload = buildPayload();
      const result = await triggerPreview(payload).unwrap();
      if (result?.pdf) {
        const byteCharacters = atob(result.pdf);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) byteNumbers[i] = byteCharacters.charCodeAt(i);
        const blob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' });
        setPdfObjectUrl(URL.createObjectURL(blob));
        setShowPdfModal(true);
      }
    } catch (error) {
      console.error('PDF preview error:', error);
      if (error.status === 404) {
        alert('No data found for the selected filters. Please adjust your filter criteria and try again.');
      } else {
        alert('Failed to generate PDF preview.');
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleGenerate = (e) => {
    e.preventDefault();

    const payload = {
      fromDate: fromDueDate || null,
      toDate: toDueDate || null,
      includeLapsed: includeLapsed ? 1 : 0, // ✅ Included
      groupIds: groupsSelected && groupsSelected.length ? groupsSelected.join(",") : null,
      sortBy: mapSortForBackend(sortBy),
      includeAddress: reportFields.address,
      includeMobile: reportFields.mobile,
      includeEmail: reportFields.email,
      includeLapsed: includeLapsed ? 1 : 0,
      filterByDate: "fup", // ✅ Always send FUP (hidden from UI)
    };

    console.log("payload", payload);
    navigate("/premium-due-report-preview", { state: { payload } });
  };


  return (
    <div className="w-full p-4">
      <div style={{ boxShadow: "0 0 8px #ccc" }} className="w-full rounded bg-white">
        <div className="p-2 border-b border-brandRed">
          <div className="flex justify-between items-center">
            <h5 className="text-brandRed font-bold m-3">Premium Due Report</h5>
            <div className="flex gap-3 items-center">
              <Mail size={38} className="text-brandRed cursor-not-allowed opacity-50" />
              <RiFileExcel2Line size={38} className="text-green-700 cursor-not-allowed opacity-50" />
              <FaFilePdf size={37} className="text-red-600 cursor-pointer hover:scale-110 transition" onClick={handlePdfPreview} title="Preview PDF" />
            </div>
          </div>
        </div>

        <form onSubmit={handleGenerate} className="w-full">
          <div className="p-6">
            <h6 className="text-gray-700 font-bold mb-3 my-3 bg-gray-100 rounded p-2">Filter Selection</h6>

            {/* Dates + Include Lapsed */}
            <div className="w-full flex flex-wrap items-end gap-6 p-2">

              {/* From Due Date */}
              <div className="flex flex-col w-1/4 min-w-[220px]">
                <label className=" text-gray-700 mb-1">From Due Date</label>
                <input
                  type="date"
                  value={fromDueDate}
                  onChange={(e) => setFromDueDate(e.target.value)}
                  className={`${fieldBase} h-10 w-full`}
                />
              </div>

              {/* To Due Date */}
              <div className="flex flex-col w-1/4 min-w-[220px]">
                <label className=" text-gray-700 mb-1">To Due Date</label>
                <input
                  type="date"
                  value={toDueDate}
                  onChange={(e) => setToDueDate(e.target.value)}
                  className={`${fieldBase} h-10 w-full`}
                />
              </div>

              {/* Include Lapsed Policies */}
              <div className="flex items-center gap-2 mb-1">
                <input
                  type="checkbox"
                  name="includeLapsed"
                  checked={includeLapsed}
                  onChange={(e) => setIncludeLapsed(e.target.checked)}
                  className="w-4 h-4"
                />
                <label className=" text-gray-700">Include Lapsed Policies</label>
              </div>
            </div>



            {/* Groups */}
            <div className="mt-6 grid grid-cols-12 gap-3 items-center p-2">
              <div className="col-span-12 lg:col-span-2">
                <div className={`${labelBase}`}>Select Groups</div>
              </div>
              <div className="col-span-12 lg:col-span-6">
                <MultiSelectInline
                  options={groupsOptions}
                  value={groupsSelected}
                  onChange={setGroupsSelected}
                  placeholder={groupsLoading ? "Loading groups..." : "Select groups..."}
                />
              </div>
              {groupsSelected.length > 0 && (
                <div className="col-span-12 lg:col-span-4  text-blue-600">
                  {groupsSelected.length} Groups Selected
                </div>
              )}
            </div>

            {/* Sorting Order */}
            <h6 className="text-gray-700 font-bold mb-3 my-3 bg-gray-100 rounded p-2">Sorting Order</h6>
            <div className="flex flex-wrap items-center gap-6 p-3">
              {["group", "member", "area", "due"].map((key) => (
                <label key={key} className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="sortBy"
                    checked={sortBy === key}
                    onChange={() => setSortBy(key)}
                    className="w-4 h-4"
                  />
                  <span className="ml-1  text-gray-700 capitalize">
                    {key === "due" ? "Due Date wise" : `${key} wise`}
                  </span>
                </label>
              ))}
            </div>

            {/* Add in Report */}
            <h6 className="text-gray-700 font-bold mb-3 my-3 bg-gray-100 rounded p-2">Add in Report</h6>
            <div className="flex gap-6 items-center flex-wrap p-3">
              {["address", "mobile", "email"].map((key) => (
                <label key={key} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={reportFields[key]}
                    onChange={() => toggleReportField(key)}
                    className="w-4 h-4"
                  />
                  <span className="ml-1  text-gray-700 capitalize">{key}</span>
                </label>
              ))}
            </div>

            <div className="flex justify-center mt-8">
              <button type="submit" className={btnPrimary}>Generate</button>
            </div>

            <div className="mt-6 text-center  text-gray-500">
              Life Insurance Premium Due Report..
            </div>
          </div>
        </form>
      </div>

      {/* PDF Preview Modal */}
      {showPdfModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-6 bg-black bg-opacity-40">
          <div className="bg-white rounded-md w-full max-w-6xl h-[80vh] overflow-hidden shadow-lg">
            <div className="flex items-center justify-between p-3 border-b">
              <div className="flex items-center gap-3">
                <FaRegFilePdf className="text-red-600" />
                <div>
                  <div className="font-semibold">PDF Preview</div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <div className="text-sm text-gray-600 mr-4">{jobStatusText}</div>
                <button className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700" onClick={handleCreateExportJobAndDownload} disabled={jobCreating}>
                  {jobCreating ? 'Preparing...' : 'Download'}
                </button>
                <button
                  className="px-3 py-1 bg-red-600 text-white rounded text-sm"
                  onClick={() => {
                    setShowPdfModal(false);
                    if (pdfObjectUrl) {
                      URL.revokeObjectURL(pdfObjectUrl);
                      setPdfObjectUrl(null);
                    }
                  }}
                >
                  Close
                </button>
              </div>
            </div>

            <div className="h-full">
              {previewResult?.isLoading || previewResult?.isFetching ? (
                <div className="flex items-center justify-center h-full">Generating preview...</div>
              ) : (
                <iframe title="PDF Preview" src={pdfObjectUrl} width="100%" height="100%" style={{ border: 'none' }} />
              )}
            </div>
          </div>
        </div>
      )}

      <ReportLoader visible={isGenerating} message="PDF report is generating..." />
    </div>
  );
}
